import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  NextOrObserver,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
  updatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { AuthUser } from '../redux/user/user.types';
import { auth } from './firebase';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const onAuthStateChangedListener = (callback: NextOrObserver<User>) =>
  onAuthStateChanged(auth, callback);

/** Maps the Firebase SDK user to the serializable snapshot kept in Redux. */
export const toAuthUser = (user: User): AuthUser => ({
  uid: user.uid,
  email: user.email,
  displayName: user.displayName,
  emailVerified: user.emailVerified,
  providerIds: user.providerData.map((provider) => provider.providerId),
  creationTime: user.metadata.creationTime ?? null,
});

/** True when the user can sign in with an email + password. */
export const hasPasswordProvider = (user: AuthUser): boolean =>
  user.providerIds.includes(EmailAuthProvider.PROVIDER_ID);

/** True when the user can sign in with Google. A user may have both providers linked. */
export const hasGoogleProvider = (user: AuthUser): boolean =>
  user.providerIds.includes(GoogleAuthProvider.PROVIDER_ID);

/** The signed-in SDK user. Throws if called while signed out. */
export const requireCurrentUser = (): User => {
  if (!auth.currentUser) {
    throw new Error('No signed-in user');
  }
  return auth.currentUser;
};

/** Confirms the user's identity with their current password before a sensitive change. */
export const reauthenticateWithPassword = async (password: string): Promise<void> => {
  const user = requireCurrentUser();
  await reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email as string, password)
  );
};

/** Confirms the user's identity with a Google popup before a sensitive change. */
export const reauthenticateWithGoogle = async (): Promise<void> => {
  await reauthenticateWithPopup(requireCurrentUser(), googleProvider);
};

/** Reauthenticates with the current password, then sets the new one. */
export const changeUserPassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  await reauthenticateWithPassword(currentPassword);
  await updatePassword(requireCurrentUser(), newPassword);
};

/**
 * Reauthenticates, then emails a confirmation link to the new address. The email only changes
 * once that link is clicked; Firebase then signs out existing sessions and emails the old
 * address a revert link.
 */
export const requestEmailChange = async (
  currentPassword: string,
  newEmail: string
): Promise<void> => {
  await reauthenticateWithPassword(currentPassword);
  await verifyBeforeUpdateEmail(requireCurrentUser(), newEmail, {
    url: `${window.location.origin}/login`,
  });
};

export const signInWithGooglePopup = () => signInWithPopup(auth, googleProvider);

export const createAuthUserWithEmailAndPassword = (email: string, password: string) =>
  createUserWithEmailAndPassword(auth, email, password);

export const signInAuthUserWithEmailAndPassword = async (email: string, password: string) => {
  await signInWithEmailAndPassword(auth, email, password);
};

/** Emails a password reset link. The link returns the user to the sign-in page. */
export const sendPasswordReset = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/login` });
};

/** Sends (or resends) the verification link. The link returns the user to their trips. */
export const sendVerificationEmail = async (): Promise<void> => {
  await sendEmailVerification(requireCurrentUser(), { url: `${window.location.origin}/trips` });
};

/**
 * Picks up a verification completed in another tab by reloading the Auth user. Returns the
 * user only when they're now verified. Each call is a network request.
 */
export const refreshEmailVerification = async (): Promise<User | null> => {
  const user = requireCurrentUser();
  await user.reload();
  return user.emailVerified ? user : null;
};
